import React, { useState, useRef, useEffect } from 'react';
import { CheckCircle, MapPin, Navigation, Loader2 } from 'lucide-react';
import Stepper from 'react-stepper-horizontal';
import { onboardBloodBankVendor, updateBloodBankVendor } from '../../../services/SalesExecutive/vendorOnboardingService';
import { useAuth } from '../../../hooks/useAuth';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { stateCityList } from '../../../data/stateCityList';

// Blood Bank fields
const bloodBankFields = [
  { name: 'agencyName', label: 'Agency Name' },
  { name: 'gstNumber', label: 'GST Number' },
  { name: 'panNumber', label: 'PAN Number' },
  { name: 'ownerName', label: 'Owner Name' },
  { name: 'completeAddress', label: 'Complete Address' },
  { name: 'nearbyLandmark', label: 'Nearby Landmark' },
  { name: 'phoneNumber', label: 'Phone Number' },
  { name: 'state', label: 'State' },
  { name: 'city', label: 'City' },
  { name: 'pincode', label: 'Pin Code' },
  { name: 'email', label: 'Email' },
  { name: 'website', label: 'Website' },
  { name: 'languageProficiency', label: 'Language Proficiency' },
  { name: 'deliveryOperationalAreas', label: 'Delivery Operational Areas' },
  { name: 'distanceLimitations', label: 'Distance Limitations' },
  { name: 'is24x7Operational', label: '24x7 Operational' },
  { name: 'isAllDaysWorking', label: 'All Days Working' },
  { name: 'bloodServicesProvided', label: 'Blood Services Provided' },
  { name: 'plateletServicesProvided', label: 'Platelet Services Provided' },
  { name: 'otherServicesProvided', label: 'Other Services Provided' },
  { name: 'acceptsOnlinePayment', label: 'Accepts Online Payment' },
  { name: 'agencyPhotos', label: 'Agency Photos' },
  { name: 'licenseFiles', label: 'License Files' },
  { name: 'registrationCertificateFiles', label: 'Registration Certificate Files' },
  { name: 'googleMapsLocation', label: 'Google Maps Location' },
];

const stepSections = [
  {
    title: 'Basic Information',
    fields: [
      'agencyName', 'ownerName', 'phoneNumber', 'email', 'website', 'completeAddress', 'nearbyLandmark', 'state', 'city', 'pincode',
    ],
  },
  {
    title: 'Business Details',
    fields: [
      'gstNumber', 'panNumber', 'languageProficiency', 'deliveryOperationalAreas', 'distanceLimitations', 'bloodServicesProvided', 'plateletServicesProvided', 'otherServicesProvided',
      'is24x7Operational', 'isAllDaysWorking', 'acceptsOnlinePayment', 'agencyPhotos', 'licenseFiles', 'registrationCertificateFiles',
    ],
  },
  {
    title: 'Location',
    fields: [
      'googleMapsLocation',
    ],
  },
];

type Step = { title: string; icon?: React.ReactNode };

type MultiValueInputProps = {
  label: string;
  name: string;
  values: string[];
  onChange: (values: string[]) => void;
};

const getFieldsForStep = (step: number) => {
  const section = stepSections[step];
  return bloodBankFields.filter(f => section.fields.includes(f.name));
};

const SimpleStepper: React.FC<{ steps: Step[]; activeStep: number }> = ({ steps, activeStep }) => {
  return (
    <div className="mb-8 pt-8">
      <Stepper
        steps={steps.map((step, idx) => ({
          title: step.title,
          icon: idx < activeStep ? <CheckCircle className="w-6 h-6" /> : undefined
        }))}
        activeStep={activeStep}
        activeColor="#2563eb"
        completeColor="#22c55e"
        defaultColor="#e5e7eb"
        activeTitleColor="#2563eb"
        completeTitleColor="#22c55e"
        defaultTitleColor="#6b7280"
        size={44}
        circleFontSize={22}
        titleFontSize={14}
        completeBarColor="#22c55e"
        defaultBarColor="#e5e7eb"
      />
    </div>
  );
};

function MultiValueInput({ label, name, values, onChange }: MultiValueInputProps) {
  const [input, setInput] = React.useState<string>('');
  const handleAdd = () => {
    const trimmed = input.trim();
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed]);
      setInput('');
    }
  };
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };
  const handleRemove = (val: string) => {
    onChange(values.filter((v: string) => v !== val));
  };
  return (
    <div>
      <div className="flex gap-2">
        <input
          type="text"
          name={name}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleInputKeyDown}
          className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-4 py-2"
          placeholder={`Add ${label.toLowerCase()}`}
        />
        <button
          type="button"
          onClick={handleAdd}
          className="px-3 py-2 rounded-lg font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-all"
        >
          Add
        </button>
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        {values.map((val: string) => (
          <span key={val} className="flex items-center bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-full text-sm">
            {val}
            <button
              type="button"
              onClick={() => handleRemove(val)}
              className="ml-2 text-red-500 hover:text-red-700"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

type ToggleSwitchProps = {
  label: string;
  name: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
};

function ToggleSwitch({ label, name, checked, onChange }: ToggleSwitchProps) {
  return (
    <div className="flex items-center gap-2">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 min-w-[140px]">{label}</label>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${checked ? 'bg-blue-600' : 'bg-gray-400 dark:bg-gray-700'}`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-5' : 'translate-x-1'}`}
        />
      </button>
      <span className="ml-2 text-xs text-gray-500">{checked ? 'Yes' : 'No'}</span>
    </div>
  );
}

type FileUpload = { id: string; name: string; file: File };
type FileUploadComponentProps = {
  fieldName: string;
  label: string;
  files: FileUpload[];
  onFilesChange: (files: FileUpload[]) => void;
};

function FileUploadComponent({ fieldName, label, files, onFilesChange }: FileUploadComponentProps) {
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles) {
      const newFiles = Array.from(selectedFiles).map((file: File) => ({
        id: Date.now() + Math.random().toString(36).substr(2, 9),
        name: file.name.replace(/\.[^/.]+$/, ""),
        file: file
      }));
      onFilesChange([...files, ...newFiles]);
    }
  };
  const handleNameChange = (id: string, newName: string) => {
    onFilesChange(files.map((f: FileUpload) => f.id === id ? { ...f, name: newName } : f));
  };
  const removeFile = (id: string) => {
    onFilesChange(files.filter((f: FileUpload) => f.id !== id));
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <input
          type="file"
          multiple
          accept="image/*,.pdf,.doc,.docx"
          onChange={handleFileChange}
          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
        />
      </div>
      {files.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Uploaded {label}:
          </div>
          {files.map((fileData: FileUpload) => (
            <div key={fileData.id} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
              <div className="flex-1">
                <input
                  type="text"
                  value={fileData.name}
                  onChange={(e) => handleNameChange(fileData.id, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                    }
                  }}
                  className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800 dark:text-white"
                  placeholder="Enter file name"
                />
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {fileData.file.name} ({(fileData.file.size / 1024).toFixed(1)} KB)
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeFile(fileData.id)}
                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

type MapComponentProps = {
  coordinates: { lat: number; lng: number } | null;
  onGetLocation: () => void;
  isGettingLocation: boolean;
};

function MapComponent({ coordinates, onGetLocation, isGettingLocation }: MapComponentProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onGetLocation}
          disabled={isGettingLocation}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Navigation className="w-4 h-4" />
          {isGettingLocation ? 'Getting Location...' : 'Get Current Location'}
        </button>
      </div>
      {coordinates && (
        <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
          <div className="p-3 bg-gray-50 dark:bg-gray-700 border-b border-gray-300 dark:border-gray-600">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Map Preview</span>
            </div>
          </div>
          <div className="w-full h-48 bg-gray-200 dark:bg-gray-600">
            <iframe
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${coordinates.lng - 0.01},${coordinates.lat - 0.01},${coordinates.lng + 0.01},${coordinates.lat + 0.01}&layer=mapnik&marker=${coordinates.lat},${coordinates.lng}`}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              title="Location Map"
              loading="lazy"
            />
          </div>
          <div className="p-3 bg-gray-50 dark:bg-gray-700 border-t border-gray-300 dark:border-gray-600">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Coordinates: {coordinates.lat.toFixed(6)}, {coordinates.lng.toFixed(6)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
const parseFilesSafely = (value: any): Array<{ name: string; url: string }> => {
  if (!value) return [];
  
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed);
        return parseFilesSafely(parsed);
      } catch (e) {
        // Fallback
      }
    }
    const name = value.split('/').pop()?.split('?')[0] || 'file';
    return [{ name, url: value }];
  }
  
  if (Array.isArray(value)) {
    return value.map((item: any) => {
      if (typeof item === 'string') {
        const name = item.split('/').pop()?.split('?')[0] || 'file';
        return { name, url: item };
      }
      if (item && typeof item === 'object') {
        const url = item.url || item.uri || '';
        const name = item.name || url.split('/').pop()?.split('?')[0] || 'file';
        return { name, url };
      }
      return { name: 'file', url: String(item) };
    }).filter(item => item.url);
  }
  
  if (typeof value === 'object') {
    const url = value.url || value.uri || '';
    if (url) {
      const name = value.name || url.split('/').pop()?.split('?')[0] || 'file';
      return [{ name, url }];
    }
  }
  
  return [];
};

const BloodBankVendorForm: React.FC = () => {
  const [step, setStep] = useState<number>(0);
  const [form, setForm] = useState<{ [key: string]: any }>({});
  const [uploadedFiles, setUploadedFiles] = useState<{ [key: string]: FileUpload[] }>({
    agencyPhotos: [],
    licenseFiles: [],
    registrationCertificateFiles: [],
  });
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [isGettingLocation, setIsGettingLocation] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [submittedData, setSubmittedData] = useState<any>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const fieldRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const { user } = useAuth();
  const { vendorId: urlVendorId } = useParams<{ vendorId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [selectedState, setSelectedState] = useState<string>('');
  const [availableCities, setAvailableCities] = useState<string[]>([]);

  // Check if this is edit mode
  const vendorData = location.state?.vendorData;
  const vendorId = urlVendorId || vendorData?.vendor?.vendorId || vendorData?.fullProfile?.vendorId;
  const isEditMode = !!vendorId;

  // Populate form data when in edit mode
  useEffect(() => {
    if (isEditMode && vendorData?.fullProfile) {
      const profile = vendorData.fullProfile;
      setForm({
        agencyName: profile.agencyName || '',
        gstNumber: profile.gstNumber || '',
        panNumber: profile.panNumber || '',
        ownerName: profile.ownerName || '',
        completeAddress: profile.completeAddress || '',
        nearbyLandmark: profile.nearbyLandmark || '',
        phoneNumber: profile.phoneNumber || '',
        state: profile.state || '',
        city: profile.city || '',
        pincode: profile.pincode || '',
        email: profile.email || '',
        website: profile.website || '',
        languageProficiency: Array.isArray(profile.languageProficiency)
          ? profile.languageProficiency
          : typeof profile.languageProficiency === 'string'
            ? profile.languageProficiency.split(',').map((s: string) => s.trim()).filter(Boolean)
            : [],
        deliveryOperationalAreas: profile.deliveryOperationalAreas || [],
        distanceLimitations: profile.distanceLimitations || '',
        is24x7Operational: profile.is24x7Operational || false,
        isAllDaysWorking: profile.isAllDaysWorking || false,
        bloodServicesProvided: profile.bloodServicesProvided || [],
        plateletServicesProvided: profile.plateletServicesProvided || [],
        otherServicesProvided: profile.otherServicesProvided || [],
        acceptsOnlinePayment: profile.acceptsOnlinePayment || false,
        googleMapsLocation: profile.googleMapsLocation || '',
      });
      // Set coordinates if location exists
      if (profile.googleMapsLocation) {
        const [lat, lng] = profile.googleMapsLocation.split(',').map((coord: string) => parseFloat(coord.trim()));
        if (!isNaN(lat) && !isNaN(lng)) {
          setCoordinates({ lat, lng });
        }
      }
      // Set uploaded files for edit mode
      const agencyPhotos = parseFilesSafely(profile.agencyPhotos);
      if (agencyPhotos.length > 0) {
        setUploadedFiles(prev => ({
          ...prev,
          agencyPhotos: agencyPhotos.map((file, idx) => ({
            id: `agency-photo-${idx}`,
            name: file.name,
            file: new File([], file.name)
          }))
        }));
      }
      const licenseFiles = parseFilesSafely(profile.licenseFiles);
      if (licenseFiles.length > 0) {
        setUploadedFiles(prev => ({
          ...prev,
          licenseFiles: licenseFiles.map((file, idx) => ({
            id: `license-file-${idx}`,
            name: file.name,
            file: new File([], file.name)
          }))
        }));
      }
      const regCertFiles = parseFilesSafely(profile.registrationCertificateFiles);
      if (regCertFiles.length > 0) {
        setUploadedFiles(prev => ({
          ...prev,
          registrationCertificateFiles: regCertFiles.map((file, idx) => ({
            id: `reg-cert-file-${idx}`,
            name: file.name,
            file: new File([], file.name)
          }))
        }));
      }
    }
  }, [isEditMode, vendorData]);

  // When editing, set state/city dropdowns
  useEffect(() => {
    if (isEditMode && vendorData?.fullProfile) {
      const profile = vendorData.fullProfile;
      if (profile.state) {
        setSelectedState(profile.state);
        const found = stateCityList.find(s => s.state === profile.state);
        setAvailableCities(found ? found.cities : []);
      }
    }
  }, [isEditMode, vendorData]);

  // Map location logic
  const getCurrentLocation = () => {
    setIsGettingLocation(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setCoordinates({ lat: latitude, lng: longitude });
          setForm(prev => ({ ...prev, googleMapsLocation: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}` }));
          setIsGettingLocation(false);
        },
        (error) => {
          setIsGettingLocation(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    } else {
      setIsGettingLocation(false);
    }
  };

  // Validation logic for each step
  const validateStep = (stepIdx: number) => {
    const section = stepSections[stepIdx];
    const errors: { [key: string]: string } = {};
    section.fields.forEach(fieldName => {
      const value = form[fieldName];
      if ([
        'agencyName', 'ownerName', 'phoneNumber', 'email', 'completeAddress', 'state', 'city', 'pincode'
      ].includes(fieldName) && (!value || (typeof value === 'string' && value.trim() === ''))) {
        errors[fieldName] = 'This field is required.';
      }
      if (fieldName === 'email' && value && !/^\S+@\S+\.\S+$/.test(value)) {
        errors[fieldName] = 'Invalid email address.';
      }
      if (fieldName === 'phoneNumber' && value && !/^\d{10}$/.test(value)) {
        errors[fieldName] = 'Enter a valid 10-digit phone number.';
      }

      if ([
        'languageProficiency', 'deliveryOperationalAreas', 'bloodServicesProvided', 'plateletServicesProvided', 'otherServicesProvided'
      ].includes(fieldName)) {
        let fieldValue = value;
        if (fieldName === 'languageProficiency' && typeof value === 'string') {
          fieldValue = value.split(',').map((s: string) => s.trim()).filter(Boolean);
        }
        if (!fieldValue || !Array.isArray(fieldValue) || fieldValue.length === 0) {
          errors[fieldName] = 'Add at least one value.';
        }
      }
    });
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      const firstErrorField = Object.keys(errors)[0];
      const ref = fieldRefs.current[firstErrorField];
      if (ref && typeof ref.scrollIntoView === 'function') {
        ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    return Object.keys(errors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(s => (s < stepSections.length - 1 ? s + 1 : s));
    }
  };
  
  const prevStep = () => setStep(s => Math.max(s - 1, 0));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = e.target;
    const { name, value, type } = target;
    if (type === 'checkbox') {
      if ((target as HTMLInputElement).checked !== undefined) {
        setForm(prev => ({ ...prev, [name]: (target as HTMLInputElement).checked }));
      }
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  // Add this function to handle Enter key on inputs
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent form submission
    }
  };

  const handleFileChange = (field: string, files: FileUpload[]) => {
    setUploadedFiles(prev => ({ ...prev, [field]: files }));
  };

  const handleReset = (fullReset = true) => {
    if (fullReset) {
      if (isEditMode) {
        // Navigate back to vendor list in edit mode
        navigate(location.pathname.startsWith('/vendors') ? '/vendors' : '/sales/vendor-onboarding');
      } else {
        // Reset form in add mode
        setForm({});
        setUploadedFiles({ agencyPhotos: [], licenseFiles: [], registrationCertificateFiles: [] });
        setCoordinates(null);
        setStep(0);
        setSubmittedData(null);
      }
    }
    setShowSuccess(false);
    setErrorMsg(null);
  };

  // Separate submit handler for explicit submission only
  const handleSubmit = async () => {
    // Validate the current step (should be last step)
    if (!validateStep(step)) return;
    
    setIsSubmitting(true);
    setErrorMsg(null);
    try {
      // Prepare payload as per API
      const payload: any = {
        phoneNumber: form.phoneNumber || '',
        email: form.email || '',
        submitted_by: user?.id || '',
        ...(isEditMode && vendorId && { vendorId: vendorId }),
        bloodBankAgencyDetails: {
          agencyName: form.agencyName || '',
          gstNumber: form.gstNumber || '',
          panNumber: form.panNumber || '',
          ownerName: form.ownerName || '',
          completeAddress: form.completeAddress || '',
          nearbyLandmark: form.nearbyLandmark || '',
          phoneNumber: form.phoneNumber || '',
          state: form.state || '',
          city: form.city || '',
          pincode: form.pincode || '',
          email: form.email || '',
          website: form.website || '',
          languageProficiency: Array.isArray(form.languageProficiency) ? form.languageProficiency.join(', ') : form.languageProficiency || '',
          deliveryOperationalAreas: form.deliveryOperationalAreas || [],
          distanceLimitations: form.distanceLimitations || '',
          is24x7Operational: !!form.is24x7Operational,
          isAllDaysWorking: !!form.isAllDaysWorking,
          bloodServicesProvided: form.bloodServicesProvided || [],
          plateletServicesProvided: form.plateletServicesProvided || [],
          otherServicesProvided: form.otherServicesProvided || [],
          acceptsOnlinePayment: !!form.acceptsOnlinePayment,
          agencyPhotos: (uploadedFiles.agencyPhotos || []).map(f => ({ 
            name: f.name, 
            url: f.file.name 
          })),
          licenseFiles: (uploadedFiles.licenseFiles || []).map(f => ({ 
            name: f.name, 
            url: f.file.name 
          })),
          registrationCertificateFiles: (uploadedFiles.registrationCertificateFiles || []).map(f => ({ 
            name: f.name, 
            url: f.file.name 
          })),
          googleMapsLocation: form.googleMapsLocation || '',
        }
      };
      
      console.log('Edit mode:', isEditMode, 'VendorId:', vendorId, 'Payload:', payload);
      let response;
      if (isEditMode && vendorId) {
        response = await updateBloodBankVendor(vendorId, payload);
      } else {
        response = await onboardBloodBankVendor(payload);
      }
      setIsSubmitting(false);
      setShowSuccess(true);
      setSubmittedData({
        ...form,
        vendorId: response.data.vendorId,
        generatedId: response.data.generatedId
      });
    } catch (error: any) {
      setIsSubmitting(false);
      setErrorMsg(error?.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'onboard'} blood bank vendor.`);
    }
  };

  const handleNextClick = () => {
    nextStep();
  };

  const handleBackClick = () => {
    prevStep();
  };

  return (
    <div className="max-w-2xl mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 mt-8">
      <div className="mb-8 pt-8">
        <SimpleStepper
          steps={stepSections.map((section, idx) => ({
            title: section.title,
            icon: idx < step || showSuccess ? <CheckCircle className="w-6 h-6" /> : undefined
          }))}
          activeStep={showSuccess ? stepSections.length : step}
        />
      </div>
      {showSuccess ? (
        <div className="flex flex-col items-center justify-center py-16">
          <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
          <h2 className="text-2xl font-bold text-green-600 mb-2">Blood Bank Vendor {isEditMode ? 'Updated' : 'Added'} Successfully!</h2>
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4 w-full max-w-md">
            <div className="text-gray-700 dark:text-gray-200 mb-1"><span className="font-semibold">Agency Name:</span> {submittedData?.agencyName}</div>
            <div className="text-gray-700 dark:text-gray-200 mb-1"><span className="font-semibold">Owner Name:</span> {submittedData?.ownerName}</div>
            <div className="text-gray-700 dark:text-gray-200 mb-1"><span className="font-semibold">Email:</span> {submittedData?.email}</div>
            <div className="text-gray-700 dark:text-gray-200 mb-1"><span className="font-semibold">Vendor ID:</span> {submittedData?.vendorId}</div>
            <div className="text-gray-700 dark:text-gray-200 mb-1"><span className="font-semibold">Generated ID:</span> {submittedData?.generatedId}</div>
          </div>
          <button
            onClick={() => handleReset(true)}
            className="mt-2 px-6 py-2 rounded-lg font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-all"
          >
            {isEditMode ? 'Back to Vendor List' : 'Add Another Blood Bank'}
          </button>
        </div>
      ) : errorMsg ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-red-100 mb-4">
            <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </div>
          <h2 className="text-2xl font-bold text-red-600 mb-2">Failed to {isEditMode ? 'Update' : 'Add'} Blood Bank Vendor</h2>
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4 w-full max-w-md text-center">
            <div className="text-gray-700 dark:text-gray-200 mb-1">{errorMsg}</div>
          </div>
          <button
            onClick={() => handleReset(false)}
            className="mt-2 px-6 py-2 rounded-lg font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-all"
          >
            Try Again
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {getFieldsForStep(step).map((field, idx, arr) => {
            // Group toggles in pairs
            if (["is24x7Operational","isAllDaysWorking","acceptsOnlinePayment"].includes(field.name)) {
              if (idx % 2 === 0 && ["is24x7Operational","isAllDaysWorking","acceptsOnlinePayment"].includes(arr[idx+1]?.name)) {
                return (
                  <div key={field.name} className="flex gap-8 mb-2">
                    <div className="flex-1">
                      <ToggleSwitch
                        label={field.label}
                        name={field.name}
                        checked={!!form[field.name]}
                        onChange={val => setForm(prev => ({ ...prev, [field.name]: val }))}
                      />
                    </div>
                    <div className="flex-1">
                      <ToggleSwitch
                        label={arr[idx+1].label}
                        name={arr[idx+1].name}
                        checked={!!form[arr[idx+1].name]}
                        onChange={val => setForm(prev => ({ ...prev, [arr[idx+1].name]: val }))}
                      />
                    </div>
                  </div>
                );
              } else if (idx % 2 === 1) {
                return null;
              } else {
                return (
                  <div key={field.name} className="mb-2">
                    <ToggleSwitch
                      label={field.label}
                      name={field.name}
                      checked={!!form[field.name]}
                      onChange={val => setForm(prev => ({ ...prev, [field.name]: val }))}
                    />
                  </div>
                );
              }
            }
            if (field.name === 'state') {
              return (
                <div key={field.name} ref={el => { fieldRefs.current[field.name] = el; }}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">State</label>
                  {fieldErrors[field.name] && (
                    <div className="text-xs text-red-500 mb-1">{fieldErrors[field.name]}</div>
                  )}
                  <select
                    name="state"
                    value={selectedState || form.state || ''}
                    onChange={e => {
                      setSelectedState(e.target.value);
                      setForm(prev => ({ ...prev, state: e.target.value, city: '' }));
                      const found = stateCityList.find(s => s.state === e.target.value);
                      setAvailableCities(found ? found.cities : []);
                    }}
                    className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-4 py-2"
                  >
                    <option value="">Select State</option>
                    {stateCityList.map(s => (
                      <option key={s.state} value={s.state}>{s.state}</option>
                    ))}
                  </select>
                </div>
              );
            }
            if (field.name === 'city') {
              return (
                <div key={field.name} ref={el => { fieldRefs.current[field.name] = el; }}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">City</label>
                  {fieldErrors[field.name] && (
                    <div className="text-xs text-red-500 mb-1">{fieldErrors[field.name]}</div>
                  )}
                  <select
                    name="city"
                    value={form.city || ''}
                    onChange={e => setForm(prev => ({ ...prev, city: e.target.value }))}
                    className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-4 py-2"
                    disabled={!selectedState}
                  >
                    <option value="">{selectedState ? 'Select City' : 'Select State First'}</option>
                    {availableCities.map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>
              );
            }
            return (
              <div key={field.name} ref={el => { fieldRefs.current[field.name] = el; }}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{field.label}</label>
                {fieldErrors[field.name] && (
                  <div className="text-xs text-red-500 mb-1">{fieldErrors[field.name]}</div>
                )}
                {['languageProficiency', 'deliveryOperationalAreas', 'bloodServicesProvided', 'plateletServicesProvided', 'otherServicesProvided'].includes(field.name) ? (
                  <MultiValueInput
                    label={field.label}
                    name={field.name}
                    values={Array.isArray(form[field.name]) ? form[field.name] : (form[field.name] ? [form[field.name]] : [])}
                    onChange={vals => setForm(prev => ({ ...prev, [field.name]: vals }))}
                  />
                ) : field.name === 'googleMapsLocation' ? (
                  <div className="space-y-3">
                    <MapComponent
                      coordinates={coordinates}
                      onGetLocation={getCurrentLocation}
                      isGettingLocation={isGettingLocation}
                    />
                    <input
                      type="text"
                      name={field.name}
                      value={form[field.name] || ''}
                      onChange={handleChange}
                      onKeyDown={handleInputKeyDown}
                      className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-4 py-2"
                      placeholder="Enter coordinates manually (e.g., 12.9716, 77.5946)"
                    />
                  </div>
                ) : field.name === 'completeAddress' ? (
                  <textarea
                    name={field.name}
                    value={form[field.name] || ''}
                    onChange={handleChange}
                    onKeyDown={handleInputKeyDown}
                    className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-4 py-2"
                    placeholder={`Enter ${field.label.toLowerCase()}`}
                    rows={3}
                  />
                ) : field.name === 'agencyPhotos' || field.name === 'licenseFiles' || field.name === 'registrationCertificateFiles' ? (
                  <FileUploadComponent
                    fieldName={field.name}
                    label={field.label}
                    files={uploadedFiles[field.name] || []}
                    onFilesChange={files => handleFileChange(field.name, files)}
                  />
                ) : (
                  <input
                    type={field.name === 'email' ? 'email' : field.name === 'website' ? 'url' : 'text'}
                    name={field.name}
                    value={form[field.name] || ''}
                    onChange={handleChange}
                    onKeyDown={handleInputKeyDown}
                    className={`mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-4 py-2 ${fieldErrors[field.name] ? 'border-red-500' : ''}`}
                    placeholder={`Enter ${field.label.toLowerCase()}`}
                  />
                )}
              </div>
            );
          })}
          <div className="flex justify-between mt-8">
            <button
              type="button"
              onClick={handleBackClick}
              disabled={step === 0}
              className="px-6 py-2 rounded-lg font-semibold bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Back
            </button>
            {step < stepSections.length - 1 ? (
              <button
                type="button"
                onClick={handleNextClick}
                className="px-6 py-2 rounded-lg font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-all"
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                className="px-6 py-2 rounded-lg font-semibold bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white shadow-md transition-all flex items-center justify-center min-w-[180px]"
                disabled={isSubmitting}
              >
                {isSubmitting && <Loader2 className="w-5 h-5 mr-2 animate-spin" />}
                {isSubmitting ? (isEditMode ? 'Updating...' : 'Adding...') : (isEditMode ? 'Update Blood Bank Vendor' : 'Add Blood Bank Vendor')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BloodBankVendorForm;